// components/portal/PortalAccountCorner.tsx
'use client';

import { memo, useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useClerk } from '@clerk/nextjs';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { KernelHandle, KernelState, QuickMenuAction } from '#types/types';
import { ChevronRight, CircleHelp, LogOut, Settings } from '@/components/ui/lucide-memo';
import UserAvatar from '@/components/UserAvatar';
import NotificationBell from '@/components/notifications/NotificationBell';
import { formatCreditsCompact } from '#lib/formatCredits';
import { GenericEntitySelector } from './GenericEntitySelector';
import { SocketStatusIndicator } from './SocketStatusIndicator';
import { runIdentitySwitchCleanup } from './portalChrome';

// The impersonation selector is driven entirely by isOpen, so its trigger and its
// (never used) selection scope are constants: inline literals rebuilt them on every
// render and broke React.memo on every entity card the selector renders.
const IMPERSONATION_SELECTED_IDS: string[] = [];
const renderNoTrigger = () => null;

// The menu's width under the expanded bar at its default 246px; the collapsed bar (52px) would
// squeeze it to 40px and clip every label.
const MENU_MIN_WIDTH = 234;

// Logging out or changing identity never depends on the kernel being there.
const withIdentitySwitch = <T,>(kernel: KernelHandle | null, perform: () => Promise<T>): Promise<T> =>
  kernel?.runIdentitySwitch(perform) ?? perform();

const NAME_STYLE = { fontSize: 14 };
const SUBTITLE_STYLE = { fontSize: 12 };

const selectOwnerFullName = (state: KernelState): string => {
  const id = state.userContext.dbUserId;
  const user = id ? state.inventory.User?.registry?.[id] : undefined;
  return [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.email || '';
};
const selectOrganisationLabel = (state: KernelState): string => {
  const id = state.userContext.organisationId;
  return (id ? state.inventory.Organisation?.registry?.[id]?.name : '') || '';
};

// The balance this user may see, or `visible: false` until an admin grants creditVisibility.
const useVisibleCredits = () => {
  const mode = useKernelStore((state) => state.userContext.permissions?.creditVisibility);
  const organisationCredits = useKernelStore((state) => state.organisationCredits);
  const amount = useMemo(() => {
    if (!organisationCredits) return null;
    if (mode === 'ORG') {
      return organisationCredits.organisationRemainingCredits
        ?? organisationCredits.remainingCredits;
    }
    // 'PERSONAL' — the balance available to this specific viewer.
    return organisationCredits.remainingCredits;
  }, [organisationCredits, mode]);
  return { visible: mode === 'ORG' || mode === 'PERSONAL', amount };
};

export const PortalAccountCorner = memo(({ isCollapsed }: { isCollapsed: boolean }) => {
  const { kernel } = useKernel();
  const { signOut } = useClerk();
  const dbUserId = useKernelStore((state) => state.userContext.dbUserId);
  const ownerFullName = useKernelStore(selectOwnerFullName);
  const organisationLabel = useKernelStore(selectOrganisationLabel);
  const [isImpersonationSelectorOpen, setIsImpersonationSelectorOpen] = useState(false);
  const cornerRef = useRef<HTMLDivElement | null>(null);

  const leaveImpersonation = useCallback(async () => {
    try {
      await withIdentitySwitch(kernel.current, () => axios.post('/api/admin', { action: 'leaveImpersonation' }));
      runIdentitySwitchCleanup(kernel.current);
      window.location.reload();
    } catch {
      kernel.current?.handleError('Could not stop impersonating');
    }
  }, [kernel]);

  const logOut = useCallback(async () => {
    try {
      await withIdentitySwitch(kernel.current, () => signOut());
    } catch {
      kernel.current?.handleError('Could not log out');
    }
  }, [kernel, signOut]);

  // ChatGPT's drop-up: the corner's width, opening upward, headed by who you are.
  const handleUserMenuClick = useCallback(() => {
    const state = kernel.current?.getState();
    const corner = cornerRef.current?.getBoundingClientRect();
    if (!state || !corner) return;
    const userId = state.userContext.dbUserId;
    const fullName = selectOwnerFullName(state);
    const organisation = selectOrganisationLabel(state);
    const menuWidth = Math.max(corner.width - 12, MENU_MIN_WIDTH);
    const openSettings = () => kernel.current?.openModal('UserSettings', { section: 'account' });
    const menuActions: QuickMenuAction[] = [
      {
        label: fullName || 'Account',
        onClick: openSettings,
        customContent: (
          // Sized to the row so the chevron sits at the right edge, as in ChatGPT.
          <span className="flex min-w-0 items-center gap-3" style={{ width: menuWidth - 32 }}>
            {userId ? (
              <UserAvatar userId={userId} name={fullName || 'User'} canEdit={false} size={28} shape="circle" />
            ) : null}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-white" style={NAME_STYLE}>{fullName || 'Account'}</span>
              {organisation ? <span className="block truncate font-normal text-[#AFAFAF]" style={SUBTITLE_STYLE}>{organisation}</span> : null}
            </span>
            <ChevronRight size={16} className="shrink-0 text-[#AFAFAF]" />
          </span>
        ),
      },
      { label: 'Settings', separatorBefore: true, icon: <Settings size={18} />, onClick: openSettings },
      { label: 'Help', separatorBefore: true, icon: <CircleHelp size={18} />, onClick: () => kernel.current?.openModal('SupportChat') },
    ];
    if (state.userContext.isImpersonating) {
      menuActions.push({ label: 'Stop impersonating', icon: <LogOut size={18} />, onClick: () => void leaveImpersonation() });
    }
    menuActions.push({ label: 'Log out', icon: <LogOut size={18} />, onClick: () => void logOut() });

    kernel.current?.setMenuData({
      position: { x: corner.left + 6, y: corner.top - 4 },
      actions: menuActions,
      skin: 'composer',
      width: menuWidth,
    });
  }, [kernel, logOut, leaveImpersonation]);

  const handleUserAvatarContextMenu = useCallback(async (event: React.MouseEvent) => {
    event.preventDefault();
    const userContext = kernel.current?.getState().userContext;
    if (userContext?.isImpersonating) {
      await leaveImpersonation();
    } else if (userContext?.isFaterOrgMember) {
      setIsImpersonationSelectorOpen(true);
    }
  }, [kernel, leaveImpersonation]);

  const handleImpersonationSelect = useCallback(async (userId: string) => {
    try {
      await withIdentitySwitch(kernel.current, () => axios.post('/api/admin', { action: 'impersonateUser', targetUserId: userId }));
      runIdentitySwitchCleanup(kernel.current);
      window.location.reload();
    } catch {
      kernel.current?.handleError('Could not start impersonating');
    } finally {
      setIsImpersonationSelectorOpen(false);
    }
  }, [kernel]);

  const handleImpersonationSelectorSelect = useCallback((userId: string) => {
    void handleImpersonationSelect(userId);
  }, [handleImpersonationSelect]);

  // Where ChatGPT prints the plan: the organisation, and the balance when this user may see it.
  const credits = useVisibleCredits();
  const accountSubtitle = [
    organisationLabel,
    credits.visible && credits.amount !== null ? `${formatCreditsCompact(credits.amount)} credits` : '',
  ].filter(Boolean).join(' · ');

  return (
    <div
      ref={cornerRef}
      data-collapsed={isCollapsed ? 'true' : 'false'}
      className="group/acct mt-auto shrink-0 px-2 py-2"
    >
      {/* Folded down to the avatar alone when the bar is collapsed. */}
      <div className="flex items-center gap-1 group-data-[collapsed=true]/acct:justify-center">
        <button
          type="button"
          onClick={handleUserMenuClick}
          onContextMenu={handleUserAvatarContextMenu}
          id="avatar-menu-button"
          aria-label="Account"
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[12px] px-2 py-1.5 text-left transition-colors hover:bg-white/[0.06] group-data-[collapsed=true]/acct:flex-none group-data-[collapsed=true]/acct:rounded-[10px] group-data-[collapsed=true]/acct:p-1"
        >
          <div className="relative">
            {dbUserId ? (
              <UserAvatar userId={dbUserId} name={ownerFullName || 'User'} canEdit={false} size={28} shape="circle" />
            ) : (
              <div className="h-[28px] w-[28px] rounded-full bg-white/10 animate-pulse" />
            )}
            <SocketStatusIndicator />
          </div>
          <span className="min-w-0 flex-1 group-data-[collapsed=true]/acct:hidden">
            <span className="block truncate text-white" style={NAME_STYLE}>{ownerFullName || 'Account'}</span>
            {accountSubtitle ? <span className="block truncate text-[#8E8E8E]" style={SUBTITLE_STYLE}>{accountSubtitle}</span> : null}
          </span>
        </button>
        <span className="shrink-0 group-data-[collapsed=true]/acct:hidden">
          <NotificationBell />
        </span>
      </div>

      <GenericEntitySelector
        type="User"
        selectedIds={IMPERSONATION_SELECTED_IDS}
        onSelect={handleImpersonationSelectorSelect}
        variant="row"
        title="Select user to impersonate"
        isOpen={isImpersonationSelectorOpen}
        onOpenChange={setIsImpersonationSelectorOpen}
        renderTrigger={renderNoTrigger}
      />
    </div>
  );
});
PortalAccountCorner.displayName = 'PortalAccountCorner';
